'use strict'

import PropTypes from 'prop-types'

import React from 'react'
import Measure from 'react-measure'
import classnames from 'classnames'

import {Table as TableComponent, Column, Cell as DataTableCell} from 'fixed-data-table'

import Cell from './Cell'
import HeaderCell from './HeaderCell/'

import 'fixed-data-table/dist/fixed-data-table.min.css'
import './Table.less'

// utils:
import {sortDataBy, getOppositeSortType, getColumnSortProperty} from './utils'

import {SORT_TYPES} from './constants'

const DEFAULT_SORT_TYPE = SORT_TYPES.ASC

const HEADER_HEIGHT = 32
const ROW_HEIGHT = 32

/**
 * A constant, default class for every Table instance
 * @constant
 * @private
 */
const COMPONENT_CLASS = 'nmc-table'

/**
 * A constant, default extra classes for every Table instance
 * @constant
 * @private
 */
const COMPONENT_ALIASES = ['nmc-table-container']

/**
 * A class name for when the class is responsive
 * @constant
 * @private
 */
const COMPONENT_RESPONSIVE_CLASS = 'nmc-table-responsive-wrapper'

/**
 * A constant default class for a table row
 * @constant
 * @private
 */
const ROW_CLASS = 'nmc-table-row'

/**
 * Validates columns - Implemented on the props declaration
 * The function returns errors if there's a data violation
 * @constant
 * @private
 * @returns {Object<Error>}
 */
const isColumnValid = (column) => {
  return !!column.header || !!column.valueProperty || !!column.cellComponent
}

/**
 * @class Components/Table
 * @description A table with sorting abilities
 * @property {String} sortBy - will sort the table by a specific value which shared betweeen the objects
 * @property {String} sortType - type of sorting order. Default is DESC, but can be set to ASC
 * @property {Boolean} responsive - will the table be responsive?
 * @property {Array<Object>} columns - array of columns
 * @property {String} column(s).header - header title
 * @property {Object<React.Component>|null} column.cellComponent - component for the header cell to be drawn on
 * @property {Boolean} column.sortable - indication for if the column can be sorted
 * @property {String|null} column.sortBy - the key on each object to sort by. If null, the column will be sorted by the header value
 * @property {Array<Object>} data - array of objects as data for the table
 * @property {Function} onRowClick - event handler function for when clicking on a row
 * @property {Number} headerHeight - height of the header
 * @property {Number} rowHeight - height for each row
 */
class Table extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      dimensions: {
        width: -1,
        height: -1
      },
      sort: {
        by: props.sortBy,
        type: this.getDefaultSortType()
      },
      data: this.props.data
    }
  }

  /**
   * Get the default sort type parameter
   * @default DEFAULT_SORT_TYPE (SORT_TYPES.ASC)
   * @returns {String}
   */
  getDefaultSortType () {
    return this.props.sortType
  }

  /**
   * Return true if the table should be responsive
   */
  isResponsive () {
    return this.props.hasOwnProperty('responsive') && this.props.responsive !== false
  }

  /**
   * Sorts the table by field name and sort-type
   * @param {String} field - field to sort the table by
   * @param {String} type - sort type, could be either SORT_TYPES.DESC ir SORT_TYPES.ASC
   * @returns {Object} this (chainable)
   */
  sortBy (field, type = DEFAULT_SORT_TYPE) {
    this.setState({
      sort: Object.assign({}, this.state.sort, {
        by: field,
        type: type
      }),
      data: sortDataBy(this.props.data, field, type)
    })
    return this
  }

  /**
   * Default click handler for a row.
   * It takes the clicked row and, if the table has its own
   * handler for click event (onRowClick), calls the click handler with it
   * @param {Object} event - an event object
   * @param {Number} rowIndex - the index of the clicked row
   */
  onRowClick = (event, rowIndex) => {
    let rowData = this.state.data[rowIndex]
    if (this.props.onRowClick) {
      this.props.onRowClick(rowData)
    }
  }

  /**
   * Returns cell data by index
   * @param {Number} index
   * @returns {Object}
   */
  getCellData (index) {
    return this.state.data[index]
  }

  /**
   * Gets the computed width of a column
   * @returns {Number}
   */
  getColumnWidth (column) {
    let width = this.getWidth()
    if (width) {
      if (this.props.columns.length) {
        if (column.width) {
          return column.width
        } else {
          let widthToSubtract = 0
          let columnsWithFixed = 0
          this.props.columns.forEach((column) => {
            if (column.width) {
              widthToSubtract += column.width
              columnsWithFixed++
            }
          })
          return (width - widthToSubtract) / (this.props.columns.length - columnsWithFixed)
        }
      }
    }
    return 0
  }

  /**
   * Generate a sort function for a column or an empty function for a non-sortable column
   * @param {Object} column - a column object
   * @returns {Function}
   */
  getHeaderClick = (column) => {
    if (column.sortable) {
      const by = getColumnSortProperty(column)
      return () => {
        const sortType = this.state.sort.by === by ? getOppositeSortType(this.state.sort.type) : this.getDefaultSortType()
        this.sortBy(by, sortType)
      }
    }
    return () => {}
  }

  getWidth () {
    return this.state.dimensions.width
  }

  getHeight () {
    return this.state.dimensions.height
  }

  /**
   * A handler to handle resize event.
   * Sets new dimensions on the state.
   */
  onMeasure = (dimensions) => {
    this.setState({dimensions})
  }

  /**
   * Updates the data everytime the props are changing
   */
  componentWillReceiveProps (props) {
    const data = this.state.sort.by ? sortDataBy(props.data, this.state.sort.by, this.state.sort.type) : props.data
    this.setState({data: data})
  }

  render () {
    const classNames = classnames(COMPONENT_CLASS, COMPONENT_ALIASES, this.props.className, {
      [COMPONENT_RESPONSIVE_CLASS]: this.isResponsive()
    })
    return (
      <Measure onMeasure={this.onMeasure} >
        <div className={classNames}>
          <TableComponent
            data={this.state.data}
            rowsCount={this.state.data.length}
            onRowClick={this.onRowClick}
            rowHeight={this.props.rowHeight}
            headerHeight={this.props.headerHeight}
            width={this.getWidth()}
            height={this.getHeight()}
            rowClassNameGetter={index => {
              return classnames(ROW_CLASS, `${ROW_CLASS}-${index}`)
            }}
            >
            {/* Rendering the columns */}
            {
              this.props.columns.map((column, i) => {
                const isSorted = this.state.sort.by === getColumnSortProperty(column)
                return (
                  <Column key={i}
                    header={
                      <HeaderCell
                        name={column.header || ''}
                        isSorted={isSorted}
                        sortType={this.state.sort.type}
                        isSortable={!!column.sortable}
                        onSortClick={this.getHeaderClick(column)}
                        value={getColumnSortProperty(column)} />
                    }
                    cell={(props) => {
                      const row = this.getCellData(props.rowIndex)
                      const text = row[column.valueProperty]
                      const CellComponent = column.cellComponent || Cell
                      return (<CellComponent clickable={!!this.props.onRowClick} columnClassName={column.className} row={row} style={column.style} {...props}>{text}</CellComponent>)
                    }}
                    width={this.getColumnWidth(column)}
                  />
                )
              })
            }
            {/* Done rendeging the columns */}
          </TableComponent>
        </div>
      </Measure>
    )
  }
}

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.objectOf((obj) => {
    if (!isColumnValid(obj)) {
      return new Error('column should contain a header, cell component or a value property')
    }
  })),
  onRowClick: PropTypes.func,
  headerHeight: PropTypes.number,
  rowHeight: PropTypes.number,
  data: PropTypes.array,
  sortBy: PropTypes.string,
  sortType: PropTypes.string,
  responsive: PropTypes.bool
}

Table.defaultProps = {
  data: [],
  columns: [],
  headerHeight: HEADER_HEIGHT,
  rowHeight: ROW_HEIGHT,
  sortType: DEFAULT_SORT_TYPE
}

Table.isColumnValid = isColumnValid

Table.SORT_TYPES = SORT_TYPES

Table.Cell = DataTableCell

export default Table



// WEBPACK FOOTER //
// ../~/@master/webinfra-frontend-components/src/components/Table/index.js